import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import posthog from 'posthog-js';
import { useLanguage } from '@/contexts/LanguageContext';

const CONSENT_KEY = 'ttt_cookie_consent';

// EU/EEA visitors must opt IN (GDPR) -> show the banner, hold tracking until they
// choose. Everyone else (US, UK, rest of world) is tracked by default (CCPA-style
// opt-out) so Meta gets the conversion data it needs. Location = device timezone,
// mirrors the same check in index.html for the pixel.
const isEuConsentRequired = () => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    return tz.startsWith('Europe/') && tz !== 'Europe/London';
  } catch {
    return false;
  }
};

// Grant or revoke Meta (Facebook) Pixel consent. The pixel loads with consent
// revoked by default (see index.html); this flips it based on the user's choice
// so no ad events reach Meta without opt-in. No-op if the pixel isn't present
// (dev, ad blocker, non-prod host).
const setPixelConsent = (granted) => {
  try {
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      window.fbq('consent', granted ? 'grant' : 'revoke');
    }
  } catch {
    /* never let tracking break the UI */
  }
};

// Upgrade PostHog from the anonymous/cookieless memory mode (see main.jsx) to
// full consented capture: make the visitor id persistent so sessions stitch
// together, ensure capturing is on, and start session recording (kept off until
// now). Safe to call more than once.
const grantAnalytics = () => {
  try {
    posthog.set_config({ persistence: 'localStorage+cookie' });
    posthog.opt_in_capturing();
    posthog.startSessionRecording?.();
  } catch {
    /* never let tracking break the UI */
  }
};

const CookieConsent = () => {
  const { t } = useLanguage();
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  // The /fb funnel keeps its "Start now" / Continue CTA pinned to the bottom
  // of the screen - a bottom-anchored banner sits exactly on top of it and
  // makes the funnel unstartable until dismissed (verified in WebKit: the
  // banner box fully covered the CTA box). On /fb/* the banner docks to the
  // top instead; everywhere else it keeps the usual bottom position.
  const onFbFunnel = location.pathname.startsWith('/fb');

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY);
    // Re-apply an explicit past decision on every load.
    if (stored === 'accepted') {
      grantAnalytics();
      setPixelConsent(true);
      return;
    }
    if (stored === 'declined') {
      posthog.opt_out_capturing();
      setPixelConsent(false);
      return;
    }
    // No decision yet.
    if (isEuConsentRequired()) {
      // GDPR: hold analytics + ad measurement until they choose; show the banner.
      posthog.opt_out_capturing();
      setPixelConsent(false);
      const timer = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(timer);
    }
    // Non-EU: track by default (opt-out model). No banner, no friction.
    grantAnalytics();
    setPixelConsent(true);
  }, []);

  const handleAccept = () => {
    localStorage.setItem(CONSENT_KEY, 'accepted');
    grantAnalytics();
    setPixelConsent(true);
    posthog.capture('cookie_consent_accepted');
    console.log('[CookieConsent] User accepted analytics + ad measurement');
    setVisible(false);
  };

  const handleDecline = () => {
    localStorage.setItem(CONSENT_KEY, 'declined');
    posthog.opt_out_capturing();
    setPixelConsent(false);
    console.log('[CookieConsent] User declined analytics + ad measurement');
    setVisible(false);
  };

  if (!visible) return null;

  // Warm, on-brand (cream + orange) banner. Kept small and low-friction: one
  // clear Accept, a quiet Decline, and a privacy link. GDPR-safe (both choices
  // work and gate PostHog + the Meta Pixel).
  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label="Cookie consent"
      className={`fixed ${onFbFunnel ? 'top-16' : 'bottom-4'} left-4 right-4 z-[9999] mx-auto max-w-sm rounded-[22px] p-4 border transition-all duration-300 ease-out`}
      style={{
        backgroundColor: '#FFFBF7',
        borderColor: '#F1E8DF',
        boxShadow: '0 10px 30px rgba(45,49,66,0.14)',
        // Bottom dock: bottom-4 alone puts the Decline button inside the home
        // indicator's swipe zone on every notched iPhone, where the system
        // takes the gesture first. Adding the inset lifts the whole banner
        // clear of it and resolves to plain 1rem where there is no indicator.
        ...(onFbFunnel
          ? { top: 'calc(env(safe-area-inset-top) + 56px)' }
          : { bottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }),
      }}
    >
      <p className="text-[15px] font-bold mb-1" style={{ color: '#3A3746' }}>
        {t('cookie.title')} 🍪
      </p>
      <p className="text-[12.5px] leading-relaxed mb-3" style={{ color: '#7A7280' }}>
        {t('cookie.body')}{' '}
        <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2" style={{ color: '#FF8C5A' }}>
          {t('cookie.learnMore')}
        </a>
      </p>

      {/* Both choices are min-h-[44px]: this banner sits on top of the funnel,
          and a measured sweep found Accept at 41px and Decline at 40px, which is
          under the touch minimum on exactly the phones the ads land on. The
          privacy link above stays as it is on purpose: it is an inline link
          inside a sentence, which the 44px rule exempts, and padding it would
          make its hit area overlap the lines of text around it. */}
      <div className="flex items-center gap-2">
        <button
          onClick={handleAccept}
          className="flex-1 min-h-[44px] flex items-center justify-center rounded-[14px] py-2.5 text-[14px] font-bold text-white touch-manipulation active:scale-[0.98] transition-transform"
          style={{ backgroundColor: '#FF8C5A', boxShadow: '0 4px 14px rgba(255,140,90,0.30)' }}
        >
          {t('cookie.accept')}
        </button>
        <button
          onClick={handleDecline}
          className="min-h-[44px] flex items-center justify-center rounded-[14px] px-4 py-2.5 text-[13px] font-medium touch-manipulation"
          style={{ color: '#A79E95' }}
        >
          {t('cookie.decline')}
        </button>
      </div>
    </div>
  );
};

export default CookieConsent;
